import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import {compose} from "mu-lambda";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import Link from "@khanacademy/wonder-blocks-link";
import {HeadingLarge, LabelLarge} from "@khanacademy/wonder-blocks-typography";
import {
    withMarkConversion,
    type WithMarkConversionProps,
    type WithoutMarkConversion,
} from "@khan/analytics-learning-time";
import Log, {Errors} from "@khan/logging";

import RouteLoadSpinner from "#app-shell-package/route-load-spinner.tsx";
import ApolloQuery from "#data-access/gql/apollo-query.tsx";
import withApollo, {
    type WithApolloProps,
    type WithoutApollo,
} from "#data-access/gql/with-apollo.tsx";
import withUserProfile, {
    type WithoutUserProfile,
    type WithUserProfileProps,
} from "#data-access/with-user-profile.tsx";
import type {FeedbackType} from "#discussion-package/types.ts";
import {
    IS_ACTIVITY_ACCESSIBLE_QUERY,
    isActivityAccessible,
    isActivityAccessibleFromQueryResults,
} from "#profile-package/is-activity-accessible.ts";
import {isHellbanned} from "#profile-package/is-hellbanned.ts";

import Error from "./error.tsx";
import {profileLessFiles, type Profile} from "./profile-util.ts";

type ProfileDiscussion =
    typeof import("../../profile-package/profile-discussion.tsx").ProfileDiscussion;

type OwnProps = {
    kaidOrUsername: string;
    profilePromise: Promise<Profile>;
    profilePath: string;
    tabName: FeedbackType | null | undefined;
    isMyPage: boolean;
    onSufficientlyUsable: () => void;
};

type Props = OwnProps &
    WithMarkConversionProps &
    WithUserProfileProps &
    WithApolloProps;

type DefaultProps = {
    tabName: Props["tabName"];
};

type State = {
    loading: boolean;
    profile: Profile | null | undefined;
    showError: boolean;
    discussionBanned: boolean;
};

class Discussion extends React.Component<Props, State> {
    // @ts-expect-error - TS2564 - Property '_isMounted' has no initializer and is not definitely assigned in the constructor.
    _isMounted: boolean;
    // @ts-expect-error - TS2564 - Property '_selectTab' has no initializer and is not definitely assigned in the constructor.
    _selectTab: () => Promise<void>;

    static defaultProps: DefaultProps = {
        tabName: null,
    };

    state: State = {
        loading: true,
        profile: undefined,
        showError: false,
        discussionBanned: false,
    };

    componentDidMount() {
        this.updateLegacyDiscussions();
    }

    componentDidUpdate(prevProps: Props) {
        if (prevProps.tabName !== this.props.tabName) {
            this._selectTab && this._selectTab();
        }

        if (prevProps.kaidOrUsername !== this.props.kaidOrUsername) {
            this.updateLegacyDiscussions();
        }

        // The profile of the user viewing this page has finished loading
        // so we update the discussions panel.
        if (prevProps.profileLoading && !this.props.profileLoading) {
            this.updateLegacyDiscussions();
        }
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    updateLegacyDiscussions() {
        const {
            profilePromise,
            markConversion,
            onSufficientlyUsable,
            gqlClient,
        } = this.props;
        this._isMounted = true;
        markConversion({id: "logged_in_homepage_visit"});
        const lessPromise = Promise.all([...profileLessFiles()]);

        const promises: [
            Promise<Profile>,
            Promise<ProfileDiscussion>,
            Promise<boolean>,
            Promise<any>,
        ] = [
            profilePromise,
            import("../../profile-package/profile-discussion.tsx").then(
                (m) => m.ProfileDiscussion,
            ),
            isActivityAccessible(gqlClient),
            lessPromise,
        ];

        Promise.all(promises)
            .then(
                // @ts-expect-error - TS2345 - Argument of type '([profile, ProfileDiscussion, isActivityAccessible]: [any, any, any]) => void' is not assignable to parameter of type '(value: [Profile, { readonly emptyMessages: { readonly "discussion-awards": { readonly private: string; readonly public: string; }; readonly questions: { readonly private: string; readonly public: string; }; readonly answers: { ...; }; readonly replies: { ...; }; readonly comments: { ...; }; }; ... 14 more ...; read...'.
                ([profile, ProfileDiscussion, isActivityAccessible]: [
                    any,
                    any,
                    any,
                ]) => {
                    if (!this._isMounted || !profile.profile) {
                        return;
                    }
                    const profileToView = profile.profile;
                    this.setState({
                        loading: false,
                        profile,
                    });
                    ProfileDiscussion.init(profileToView, gqlClient);
                    this._selectTab = () => {
                        const {tabName, profile} = this.props;
                        if (tabName) {
                            return ProfileDiscussion.populateTab(tabName);
                        }
                        // 'profile' is for current user viewing the page
                        return ProfileDiscussion.populateSummary(
                            profile,
                            isActivityAccessible,
                            this.state.discussionBanned,
                        );
                    };
                    isHellbanned(gqlClient, profileToView.get("kaid"))
                        .then((discussionBanned) => {
                            if (!this._isMounted) {
                                return;
                            }
                            if (
                                this.state.discussionBanned !== discussionBanned
                            ) {
                                this.setState({discussionBanned});
                            }
                            this._selectTab();
                        })
                        .then(() => onSufficientlyUsable());
                },
            )
            .catch((error) => {
                if (this._isMounted) {
                    Log.error(
                        "Error initializing Discussion profile:",
                        Errors.Internal,
                        {cause: error},
                    );
                    this.setState({showError: true});
                }
            });
    }

    shouldShowModeratorPanel(): boolean {
        const {profile} = this.state;

        const userProfile = this.props.profile;

        const canHellbanOrMessageUsers =
            userProfile &&
            (userProfile.get("canHellban") ||
                userProfile.get("canMessageUsers"));

        const isSelf =
            profile && profile.profile && profile.profile.get("isSelf");

        return Boolean(canHellbanOrMessageUsers && !isSelf);
    }

    renderTabSection(id: string): React.ReactNode {
        const {tabName} = this.props;

        return (
            <div
                id={`discussion-${id}`}
                className={`discussion-tab-content ${css(
                    tabName !== id && styles.hide,
                )}`}
            >
                <div id={id} />

                <div className="discussion-placeholder" />
            </div>
        );
    }

    render(): React.ReactNode {
        const {profilePath, tabName, isMyPage} = this.props;
        const {loading, showError} = this.state;

        const selected = (tab: string) => (tabName === tab ? "selected" : "");

        if (showError) {
            return <Error />;
        }

        return (
            <View style={styles.container}>
                <LabelLarge style={styles.breadcrumb}>
                    <Link href={profilePath}>{t`< Profile`}</Link>
                </LabelLarge>
                <HeadingLarge style={styles.heading}>
                    {isMyPage ? t`My discussions` : t`Discussions`}
                </HeadingLarge>
                {loading && <RouteLoadSpinner />}
                {/* The following markup was taken directly from
                    profile.handlebars.js.  The ProfileDiscussion object is
                    responsible for populating things in this section and
                    most of this is meant to be opaque.

                    We cannot just use Profile.renderTabChrome because it
                    includes naked navigation links which does not work well
                    with ReactRouter.

                    Someday, it would be very nice if we re-design and
                    re-write this entire Profile/Discussions UI. */}
                <div
                    id="tab-content-discussion"
                    className={`lihp-discussion ka-tab-content ${
                        loading ? css(styles.hide) : ""
                    }`}
                >
                    <div className="graph-picker">
                        <ApolloQuery
                            query={IS_ACTIVITY_ACCESSIBLE_QUERY}
                            fetchPolicy="cache-and-network"
                        >
                            {({loading, data}) =>
                                isActivityAccessibleFromQueryResults(data) &&
                                t`Shows your participation in community discussions.`
                            }
                        </ApolloQuery>

                        <ul className="tabrow pure-hidden-xs pure-hidden-sm">
                            <li className={`type discussion ${selected("")}`}>
                                <span className="graph-link visited-no-recolor discussion-link">
                                    <Link href={`${profilePath}/discussion`}>
                                        {t`Summary`}
                                    </Link>
                                </span>
                            </li>
                            <li
                                className={`type questions ${selected(
                                    "questions",
                                )}`}
                            >
                                <span className="graph-link visited-no-recolor discussion-link">
                                    <Link
                                        href={`${profilePath}/discussion/questions`}
                                    >
                                        {t`Questions`}
                                    </Link>
                                </span>
                            </li>
                            <li
                                className={`type projectquestions ${selected(
                                    "projectquestions",
                                )}`}
                                style={{display: "none"}}
                                data-show-if-any="true"
                            >
                                <span className="graph-link visited-no-recolor discussion-link">
                                    <Link
                                        href={`${profilePath}/discussion/projectquestions`}
                                    >
                                        {t`Project Help Requested`}
                                    </Link>
                                </span>
                            </li>
                            <li
                                className={`type projectanswers ${selected(
                                    "projectanswers",
                                )}`}
                                style={{display: "none"}}
                                data-show-if-any="true"
                            >
                                <span className="graph-link visited-no-recolor discussion-link">
                                    <Link
                                        href={`${profilePath}/discussion/projectanswers`}
                                    >
                                        {t`Project Help Provided`}
                                    </Link>
                                </span>
                            </li>
                            <li
                                className={`type answers ${selected(
                                    "answers",
                                )}`}
                            >
                                <span className="graph-link visited-no-recolor discussion-link">
                                    <Link
                                        href={`${profilePath}/discussion/answers`}
                                    >
                                        {t`Answers`}
                                    </Link>
                                </span>
                            </li>
                            <li
                                className={`type replies ${selected(
                                    "replies",
                                )}`}
                            >
                                <span className="graph-link visited-no-recolor discussion-link">
                                    <Link
                                        href={`${profilePath}/discussion/replies`}
                                    >
                                        {t`Comments`}
                                    </Link>
                                </span>
                            </li>
                            <li
                                className={`type comments ${selected(
                                    "comments",
                                )}`}
                            >
                                <span className="graph-link visited-no-recolor discussion-link">
                                    <Link
                                        href={`${profilePath}/discussion/comments`}
                                    >
                                        {t`Tips & Thanks`}
                                    </Link>
                                </span>
                            </li>
                        </ul>
                    </div>

                    <div className="discussion-placeholder" />

                    <div
                        id="discussion-summary"
                        className={`pure-g pure-g--gutter ${css(
                            tabName !== null && styles.hide,
                        )}`}
                    >
                        <div className="pure-u-1 pure-u-lg-3-5">
                            {/* Discussion -> Questions */}
                            <div
                                className="discussion-block"
                                id="questions-block"
                            />

                            {/* Discussion -> Project Help Requested */}
                            <div
                                className="discussion-block"
                                id="projectquestions-block"
                            />

                            {/* Discussion -> Project Help Provided */}
                            <div
                                className="discussion-block"
                                id="projectanswers-block"
                            />

                            {/* Discussion -> Answers */}
                            <div
                                className="discussion-block"
                                id="answers-block"
                            />

                            {/* Discussion -> Replies */}
                            <div
                                className="discussion-block"
                                id="replies-block"
                            />

                            {/* Discussion -> Comments */}

                            <div
                                className="discussion-block"
                                id="comments-block"
                            />
                        </div>

                        <div className="pure-u-1 pure-u-lg-2-5">
                            {this.shouldShowModeratorPanel() && (
                                <div id="discussion-moderation-mount" />
                            )}

                            {/* Discussion > Badges */}
                            <div
                                className="discussion-block"
                                id="discussion-awards-block"
                            >
                                <h2 className="discussion-group-header">
                                    {t`Badges`}
                                </h2>
                                <div id="discussion-awards" />
                            </div>

                            {/* Discussion > Statistics */}
                            <div className="discussion-block">
                                <h2 className="discussion-group-header">
                                    {t`Statistics`}
                                </h2>
                                <span id="discussion-statistics-block" />
                            </div>
                        </div>
                    </div>

                    <div>
                        {this.renderTabSection("questions")}
                        {this.renderTabSection("projectquestions")}
                        {this.renderTabSection("projectanswers")}
                        {this.renderTabSection("answers")}
                        {this.renderTabSection("replies")}
                        {this.renderTabSection("comments")}
                    </div>
                </div>
            </View>
        );
    }
}

const styles = StyleSheet.create({
    container: {
        marginBottom: 32,
    },
    hide: {
        display: "none",
    },
    breadcrumb: {
        marginBottom: 4,
    },
    heading: {
        marginBottom: 0,
    },
});

type ExportProps = WithoutApollo<
    WithoutMarkConversion<
        WithoutUserProfile<
            JSX.LibraryManagedAttributes<
                typeof Discussion,
                React.ComponentProps<typeof Discussion>
            >
        >
    >
>;

export default compose(
    withMarkConversion,
    withUserProfile,
    withApollo,
)(Discussion) as React.ComponentType<ExportProps>;
